'use client'

import {useEffect, useRef, useState} from 'react'
import {cx} from '@local/utils/cx'
import {AnimationControls, timeline} from 'motion'
import {useDevice, useRaf} from '@local/hooks'
import {SanityImageType} from '@local/types'
import {Lottie, ComingSoonLogo, ComingSoonText, AutoSlideshow, ImageStack} from '@local/ui/components'
import {calcIndexRotation} from '@local/utils/maths'
import {quadOut, quartInOut, quartOut, quintInOut} from 'eases'

export interface ComingSoonContentProps {
	surtitle: string;
	text: string;
	categories: Array<{title: string, image: SanityImageType}>
}

const LOOP_DURATION = 5000
const MARQUEE_CHILDREN_COUNT = 3
const TEXT_STAGGER = 0.05

export const ComingSoonContent = ({surtitle, text, categories}: ComingSoonContentProps) => {
	const _images = categories.map(item => item.image)

	const progress = useRef<number>(0)
	const tl = useRef<AnimationControls>(null)

	const [currentIndex, setCurrentIndex] = useState<number>(0)
	const slideshowRef = useRef<HTMLDivElement>(null)
	const imageStackRef = useRef<HTMLDivElement>(null)
	const lottieWrapperRef = useRef<HTMLDivElement>(null)
	const logoRef = useRef<HTMLSpanElement>(null)
	const textRef = useRef<HTMLDivElement>(null)
	const device = useDevice()

	const lottieMotion = useRef<boolean>(false)
	const [isLetterMotion, setLetterMotion] = useState<boolean>(false)
	const [textMotion, setTextMotion] = useState<boolean>(false)
	const [startSlideshow, setStartSlideshow] = useState<boolean>(false)
	const startSlideshowRef = useRef<boolean>(false)

	const handleSlideshowChange = (index: number) => {
		const _index = index % categories.length
		setCurrentIndex(_index)
		// @ts-expect-error Property next does not exist on type 'HTMLDivElement'
		slideshowRef.current.next()
		// @ts-expect-error Property next does not exist on type 'HTMLDivElement'
		imageStackRef.current?.next?.(_index)
	}

	useRaf((_, deltaTime) => {
		if (tl.current && tl.current.currentTime) {
			if (tl.current.currentTime > 0.75) lottieMotion.current = true
			if (tl.current.currentTime > 0.5) setLetterMotion(true)
			if (tl.current.currentTime > 7.5) setTextMotion(true)
			if (tl.current.currentTime >= tl.current.duration + 1.15 && !startSlideshowRef.current) {
				setStartSlideshow(true)
				startSlideshowRef.current = true
				handleSlideshowChange(currentIndex + 1)
			}
		}

		if (lottieMotion.current) {
			progress.current += deltaTime / LOOP_DURATION
			if (progress.current >= 1) {
				progress.current = 0
				if (startSlideshowRef.current) {
					handleSlideshowChange(currentIndex + 1)
				}
			}
		}
	})

	useEffect(() => {
		const startMotion = async () => {
			if (logoRef.current && textRef.current && imageStackRef.current && imageStackRef.current.items) {
				const stackImages = imageStackRef.current.items

				// logoRef.current.style.transform = `translate3d(0, calc(50svh - ${device === 'desktop' ? 2.5 : 2}rem*2), 0) scale(${device === 'desktop' ? 3.25 : 2.125})`
				logoRef.current.style.transform = `translate3d(0, calc(var(--screenHeightMin)/2 - ${device === 'desktop' ? 2.5 : 2}rem*2), 0) scale(${device === 'desktop' ? 3.25 : 2.125})`

				const sequence = [
				// logo animation start
					[logoRef.current, {opacity: 1}, {duration: 1.5, easing: quadOut}],
					[logoRef.current, {transform: 'translate3d(0,0,0) scale(1)'}, {duration: 1.5, at: 0.75, easing: quintInOut}],
					// logo animation end

					// lottie opacity animation
					[lottieWrapperRef.current, {opacity: 1}, {duration: 1, at: 4, easing: quartOut}],

					// text animation start
					[
						textRef.current,
						{opacity: 1, transform: 'scaleX(1) scaleY(1) translateY(0) translateZ(0)', visibility: 'visible'},
						{duration: 1.5, at: 0.80, easing: quartInOut},
					],
					[
						textRef.current,
						{opacity: 0, transform: 'scaleX(0.9) scaleY(0.9) translateZ(0)', visibility: 'hidden'},
						{
							duration: 1.4,
							delay: (text.replace(/\s/g, '').length - 1) * TEXT_STAGGER + 0.3,
							easing: quintInOut,
						},
					],
					// text animation end

					// image stack animation start
					...stackImages.map((item, index) => ([
						[item,
							{transform: `scale3d(0,0,0) translateZ(0) rotate(${-calcIndexRotation(index, stackImages.length)}deg)`, opacity: 1},
							{duration: 0},
						],
						[item,
							{transform: `scale3d(1,1,1) translateZ(0) rotate(${-calcIndexRotation(index, stackImages.length)}deg)`, opacity: 1},
							{
								duration: 2,
								at: '<',
								delay: (stackImages.length - index - 1) * 0.15,
								easing: quartInOut,
							},
						],
					])).flat().sort((a, b) => a[2].duration - b[2].duration),
				// image stack animation end
				]

				tl.current = timeline(sequence, {endDelay: 2.5})
			}
		}
		startMotion()
	}, [logoRef.current, textRef.current, imageStackRef.current])

	return (
		<>
			{/* backdrop */}
			<div className='h-screen w-full bg-white-off absolute inset-0 -z-1 overflow-clip opacity-0 lg-max:pb-30' ref={lottieWrapperRef}>
				<Lottie
					progress={progress}
					className={cx('lg-max:scale-[1.8]', 'h-full w-full')}
					filePath='lottie/Pulse-lottie-v5.json'
					autoplay
					loop />
			</div>

			{/* Logo */}
			<ComingSoonLogo logoRef={logoRef} />

			{/* Text */}
			<ComingSoonText surtitle={surtitle} text={text} containerRef={textRef} letterMotion={isLetterMotion} />

			{/* Slideshow */}
			<div className='absolute w-full h-full overflow-clip'>
				<div className='relative w-full h-full md-max:h-[80svh] xs-wh-max:h-[70svh]'>
					<AutoSlideshow
						amount={MARQUEE_CHILDREN_COUNT}
						duration={5}
						startPlaying={startSlideshow}
						ref={slideshowRef}
						className='text-24 lg:text-100 !leading-none uppercase text-grey-5'>
						{categories.map((item, index) => (
							<div
								key={index}
								className={cx(
									'flex justify-center items-center',
									'transform-gpu transition-[transform,opacity] overflow-clip ease-quart-out duration-[750ms]',
									textMotion ? 'opacity-100 scale-100' : 'opacity-0 scale-90',
								)}>
								<h2
									className={cx(
										'whitespace-nowrap transition-opacity ease-quad-inout',
										startSlideshow ? 'duration-[2500ms]' : 'duration-0',
										currentIndex === index ? 'opacity-100' : 'opacity-10',
									)}>
									{item.title}
								</h2>
							</div>
						))}
					</AutoSlideshow>
					<ImageStack images={_images} ref={imageStackRef} startPlaying={startSlideshow} />
				</div>
			</div>
		</>
	)
}
